import { CONTACT_CONFIG, CONTACT_DETAILS } from '../config/constants.js'

/* ── Types ─────────────────────────────────────────────────────────── */

export interface ServiceBlock {
  title: string
  intro: string
  bullets: string[]
}

export interface ProcessStep {
  num: string
  title: string
  description: string
}

export interface NavLink {
  label: string
  href: string
}

/* ── SEO ───────────────────────────────────────────────────────────── */

export const PAGE_TITLE = 'Farzad Bayat — Norway Fixer & Production Services'
export const PAGE_DESCRIPTION =
  'Oslo-based fixer and local producer for international shoots in Norway. Permits, locations, logistics, crew sourcing, translation, and on-ground coordination.'
export const PAGE_KEYWORDS =
  'Norway fixer, Oslo fixer, local producer Norway, production coordinator Norway, filming in Norway, production services Norway'

export const PAGE_OG_IMAGE = `${CONTACT_CONFIG.WEBSITE_URL}/fixer/farzadfixer.png`
export const CONTACT_PORTRAIT_SRC = '/fixer/farzadfixer3.png'

/* ── Contact helpers ───────────────────────────────────────────────── */

export const email = CONTACT_DETAILS.find(c => c.label === 'Email')?.value ?? CONTACT_CONFIG.SUPPORT_EMAIL
export const phone = CONTACT_DETAILS.find(c => c.label === 'Phone')?.value ?? '+47 94 44 64 46'
export const phoneTel = CONTACT_DETAILS.find(c => c.label === 'Phone')?.href ?? 'tel:+4794446446'
export const websiteUrl = CONTACT_CONFIG.WEBSITE_URL
export const FIXER_PAGE_PATH = '/fixer'
export const PAGE_URL = `${websiteUrl}${FIXER_PAGE_PATH}`
export const CONTACT_WEBSITE_DISPLAY = 'farzadbayat.com/fixer'
export const bookingUrl = CONTACT_CONFIG.SCHEDULING.BOOKING_URL
export const whatsappUrl = 'https://wa.me/4794446446'
export const linkedinUrl = 'https://www.linkedin.com/in/farzad-bayat/'

const mailtoBody = `Hi Farzad,

I need production support in Norway.

Production type:
Shoot dates:
Preferred region:
Crew size:
What we need help with:

Best,`

export const productionBriefMailto = `mailto:${email}?subject=${encodeURIComponent('Norway Production Brief')}&body=${encodeURIComponent(mailtoBody)}`

/** Same interest label sent to `/api/contact` as the main site contact form pattern */
export const FIXER_BRIEF_INTEREST = 'Norway fixer — production brief'
export const FIXER_BRIEF_FORM_ID = 'fixer-brief-form'
export const FIXER_BRIEF_FORM_HASH = `#${FIXER_BRIEF_FORM_ID}`

/* ── Media ─────────────────────────────────────────────────────────── */

export const VIMEO_SHOWCASE_URL = 'https://vimeo.com/showcase/12289452/embed2'
export const FEATURED_VIDEO_URL = 'https://youtu.be/2SBsNJvN1ws'
export const FEATURED_VIDEO_THUMB = 'https://img.youtube.com/vi/2SBsNJvN1ws/hqdefault.jpg'

/* ── Navigation ────────────────────────────────────────────────────── */

export const NAV_LINKS: NavLink[] = [
  { label: 'Services', href: '#services' },
  { label: 'Showcase', href: '#showcase' },
  { label: 'Production', href: '#production' },
  { label: 'For', href: '#for' },
  { label: 'Process', href: '#process' },
  { label: 'Coverage', href: '#coverage' },
  { label: 'FAQ', href: '#faq' },
  { label: 'Contact', href: '#contact' },
]

/** Desktop header — primary anchors */
export const HEADER_NAV_LINKS: NavLink[] = [
  { label: 'Services', href: '#services' },
  { label: 'Showcase', href: '#showcase' },
  { label: 'Production', href: '#production' },
  { label: 'FAQ', href: '#faq' },
  { label: 'Contact', href: '#contact' },
]

/* ── Hero ──────────────────────────────────────────────────────────── */

export const HERO = {
  overline: 'Oslo · Norway · International shoots',
  title: 'Film in Norway with a local producer who understands international production.',
  subtitle:
    'Local production support for ambitious shoots — crew, logistics, access, permits, and on-set problem solving.',
  locationLine: 'Based in Norway. Working worldwide.',
  sendBriefLabel: 'Send a Production Brief',
  bookLabel: 'Book a Call',
  callLabel: 'Call',
  viewWorkLabel: 'View Work',
  body:
    'Based in Oslo, I provide fixer services, local production support, location coordination, permits, logistics, crew sourcing, vendor coordination, translation, and on-ground problem solving.',
  background:
    'I am Norwegian with an extensive production background across film, TV, branded content, corporate productions, and music videos — including years of international work outside Norway, so I understand how outside crews expect schedules, communication, and on-set pace to run.',
  ctaPrompt:
    'Need a fixer, producer, or production coordinator in Norway? Send your brief and I’ll help you understand what is possible, what needs permits, what it may require, and how to move forward.',
  asideTitle: 'Norwegian, Oslo-based, with production experience in Norway and internationally.',
  asideBody:
    'I help outside teams move faster, avoid local friction, and get clean practical answers before and during production.',
}

export interface HeroHighlight {
  id: string
  title: string
  description: string
}

export const HERO_HIGHLIGHTS: HeroHighlight[] = [
  {
    id: 'logistics',
    title: 'On-set logistics',
    description: 'Equipment, transport, accommodation, and production-day coordination.',
  },
  {
    id: 'locations',
    title: 'Location access',
    description: 'Scouting, permits, and connections with owners and authorities.',
  },
  {
    id: 'crew',
    title: 'Crew coordination',
    description: 'Trusted local crew, vendors, and communication on the ground.',
  },
]

/* ── Services ──────────────────────────────────────────────────────── */

export const SERVICES_INTRO = {
  number: '01 · Services',
  title: 'Quietly effective local support.',
  copy:
    'Fixing is not about adding another layer. It is about removing friction: finding the right people, getting access, making local communication clear, and keeping production days realistic.',
}

export interface ServiceSummary {
  id: string
  title: string
  description: string
}

/** Scannable capability cards (preview-style); full detail in accordion below */
export const SERVICE_SUMMARIES: ServiceSummary[] = [
  {
    id: 'fixer',
    title: 'Fixer services',
    description:
      'Local research, location support, contributors, permissions, translation, cultural context, and shoot-day problem solving.',
  },
  {
    id: 'producer',
    title: 'Local producer support',
    description:
      'Production planning, schedules, vendor coordination, crew sourcing, budget support, local communication, and on-ground management.',
  },
  {
    id: 'locations',
    title: 'Locations and access',
    description:
      'Urban, corporate, industrial, maritime, nature, and private locations with practical notes on timing, access, weather, routes, and restrictions.',
  },
  {
    id: 'permits',
    title: 'Permits and permissions',
    description:
      'Municipality contact, private property agreements, public-space guidance, drone coordination, location releases, and authority communication.',
  },
  {
    id: 'crew',
    title: 'Crew, vendors, logistics',
    description:
      'Camera, sound, production assistants, drivers, translators, drone operators, equipment rental, catering, transport, call sheets, and backup plans.',
  },
  {
    id: 'research',
    title: 'Research and contributors',
    description:
      'Story research, expert sourcing, interview subjects, company outreach, participant coordination, casting support, and Norwegian-language communication.',
  },
]

export const CORE_SERVICES: ServiceBlock[] = [
  {
    title: 'Fixer services in Norway',
    intro: 'Local support for international crews filming in Norway.',
    bullets: [
      'Local research and planning',
      'Location suggestions and access support',
      'Contact with local authorities, businesses, and private owners',
      'Contributor and interview coordination',
      'Translation and local communication',
      'Cultural and practical guidance',
      'On-ground support during shoot days',
      'Problem solving before and during production',
    ],
  },
  {
    title: 'Local producer support',
    intro: 'Hands-on production support for companies that need a trusted person on the ground in Norway.',
    bullets: [
      'Production planning',
      'Budget support',
      'Local production schedules',
      'Crew coordination',
      'Vendor sourcing',
      'Local production management',
      'Shoot-day coordination',
      'Communication between client, crew, talent, and locations',
      'Production wrap-up and local follow-up',
    ],
  },
  {
    title: 'Location research & coordination',
    intro:
      'Norway has strong visual variety, but access and planning matter. I help identify locations that match the creative direction and practical needs of the shoot.',
    bullets: [
      'Urban locations in Oslo and surrounding areas',
      'Fjords, forests, mountains, coastlines, and rural areas',
      'Corporate offices and industrial locations',
      'Homes, studios, streets, and public spaces',
      'Location contact and access checks',
      'Practical notes on timing, weather, travel, light, and permissions',
      'Coordination with property owners and local contacts',
    ],
  },
  {
    title: 'Permits & local permissions',
    intro:
      'Filming in Norway can involve different permissions depending on location, crew size, drone use, public space, traffic, private property, and commercial use.',
    bullets: [
      'Municipality contact',
      'Public space filming guidance',
      'Private property agreements',
      'Drone filming coordination',
      'Police or road-related coordination when relevant',
      'Location release support',
      'Practical planning around restrictions and timelines',
    ],
  },
  {
    title: 'Crew, vendors & equipment',
    intro: 'I help source reliable local production resources based on the needs of the project.',
    bullets: [
      'Camera crew',
      'Sound recordists',
      'Production assistants',
      'Drivers',
      'Translators',
      'Hair and makeup',
      'Stylists',
      'Photographers',
      'Drone operators',
      'Equipment rental',
      'Studios',
      'Transport providers',
      'Catering and local support services',
    ],
  },
  {
    title: 'Logistics & shoot planning',
    intro: 'International productions can lose time fast without local structure. I help keep the moving parts clear and manageable.',
    bullets: [
      'Daily production schedules',
      'Transport planning',
      'Hotel and accommodation coordination',
      'Airport pickup and local travel planning',
      'Call sheets',
      'Weather and timing considerations',
      'Backup plans',
      'Local route planning',
      'On-set coordination',
    ],
  },
  {
    title: 'Research, casting & contributors',
    intro:
      'For documentaries, branded content, interviews, and corporate stories, finding the right people is often the hardest part.',
    bullets: [
      'Local story research',
      'Expert sourcing',
      'Interview subject research',
      'Business and organization outreach',
      'Contributor coordination',
      'Casting support',
      'Background checks where appropriate',
      'Communication with Norwegian-speaking participants',
    ],
  },
  {
    title: 'Translation & cultural support',
    intro:
      'I help bridge the communication gap between international crews and Norwegian contributors, companies, locations, and authorities.',
    bullets: [
      'Norwegian-English communication',
      'Translation during planning and shoot days',
      'Email and phone communication with local contacts',
      'Cultural guidance',
      'Practical advice on how to approach Norwegian companies, authorities, and private individuals',
    ],
  },
]

/* ── Showcase ──────────────────────────────────────────────────────── */

export interface CaseCredit {
  project: string
  role: string
  note: string
}

export const CASE_CREDITS: CaseCredit[] = [
  {
    project: 'International branded content & corporate films',
    role: 'Producer / fixer',
    note: 'Interview-led, location-heavy, and multi-vendor shoots for outside crews.',
  },
  {
    project: 'Documentary, TV, and factual productions',
    role: 'Local production support',
    note: 'Contributor access, translation, permits, and on-ground coordination across Norway.',
  },
]

export const SHOWCASE = {
  number: '02 · Showcase',
  title: 'Production track record.',
  copy:
    'International production across Nordic and global environments — from high-view commercial work to broader film and branded content.',
  featured: {
    projectTitle: 'Arash feat. Helena – One Night In Dubai',
    role: 'Producer',
    views: 'Over 200 million views',
    description:
      'My last production in Dubai was Arash feat. Helena – One Night In Dubai, where I was credited as Producer. The video has over 200 million views and reflects my background in high-pressure international production environments.',
    watchLabel: 'Watch on YouTube',
    videoUrl: FEATURED_VIDEO_URL,
    thumbUrl: FEATURED_VIDEO_THUMB,
  },
  reel: {
    title: 'Selected reel',
    copy: 'Commercial, branded, and music video work from my broader production background.',
  },
}

/* ── Production lifecycle ─────────────────────────────────────────── */

export interface ProductionLifecycleColumn {
  phase: string
  title: string
  description: string
}

export const PRODUCTION_LIFECYCLE = {
  number: '03 · Production support',
  title: 'Built for crews that need clarity fast.',
  copy:
    'International productions often need one reliable person who can translate the local reality into clear production decisions — before, during, and after the shoot.',
  columns: [
    {
      phase: 'Before shoot',
      title: 'Feasibility and planning',
      description:
        'Check locations, access, timing, local constraints, permits, logistics, and what can realistically be done before crew travel.',
    },
    {
      phase: 'Local setup',
      title: 'Crew and vendor sourcing',
      description:
        'Find and coordinate reliable local people, rental options, transport, support services, and practical production resources.',
    },
    {
      phase: 'On location',
      title: 'Shoot-day coordination',
      description:
        'Handle local communication, timing, contributors, vendors, movement, translation, and day-of problem solving.',
    },
    {
      phase: 'After shoot',
      title: 'Local wrap-up',
      description:
        'Follow up with locations, vendors, receipts, releases, remaining logistics, and production close-out details.',
    },
  ] satisfies ProductionLifecycleColumn[],
}

export const INTERNATIONAL_WEDGE = {
  kicker: 'International crews',
  title: 'Norwegian fixer for teams that shoot across borders.',
  copy:
    'Most of my work is with crews and agencies based outside Norway. I translate the local reality — permits, locations, crew, vendors, weather, and timing — into clear production decisions so international teams can keep moving without losing pace or clarity.',
}

export const TYPICAL_NEEDS: string[] = [
  'Local research and fast feasibility checks',
  'Location access and permission handling',
  'Norwegian–English translation and on-set communication',
  'Local crew, drivers, vendors, and equipment',
  'Contributor and interview coordination',
  'On-ground support during production days',
]

/* ── For ───────────────────────────────────────────────────────────── */

export const FOR_SECTION = {
  number: '04 · For',
  title: 'For international teams filming in Norway.',
}

export const PRODUCTION_TYPES: string[] = [
  'Documentary productions',
  'Corporate films',
  'Commercials',
  'Music videos',
  'Branded content',
  'TV segments',
  'Social media campaigns',
  'Interviews',
  'News and current affairs shoots',
  'Travel and tourism content',
  'Event filming',
  'Startup and technology stories',
  'Industrial and maritime productions',
]

export const WHO_THIS_IS_FOR: string[] = [
  'International production companies filming in Norway',
  'Agencies producing branded content',
  'Broadcasters and TV teams',
  'Documentary producers',
  'Broadcasters and TV teams filming in Scandinavia',
  'Music video productions',
  'Corporate video teams',
  'Commercial producers',
  'Journalists and media teams',
  'Small crews needing a reliable local contact',
  'Larger productions needing local production support',
]

/* ── Process ───────────────────────────────────────────────────────── */

export const PROCESS_INTRO = {
  number: '05 · Process',
  title: 'Send the brief. Get the local reality.',
  copy:
    'I can help you understand what is possible, what needs more time, what needs permission, and what should be handled before the crew arrives.',
}

export const PROCESS_STEPS: ProcessStep[] = [
  {
    num: '01',
    title: 'Send the brief',
    description:
      'Send the project details, dates, production type, location needs, crew size, and what kind of support you need.',
  },
  {
    num: '02',
    title: 'Local feasibility check',
    description:
      'I review what is realistic in Norway, including locations, permits, crew, logistics, timing, and possible risks.',
  },
  {
    num: '03',
    title: 'Planning and coordination',
    description:
      'I help build the local plan, contact locations, source crew and vendors, support permits, and prepare the shoot structure.',
  },
  {
    num: '04',
    title: 'Shoot-day support',
    description:
      'I can be on the ground to coordinate, translate, solve problems, manage local details, and keep the production moving.',
  },
  {
    num: '05',
    title: 'Wrap-up',
    description:
      'After the shoot, I can support with local follow-up, vendor communication, receipts, releases, and remaining production details.',
  },
]

/* ── Coverage ──────────────────────────────────────────────────────── */

export const COVERAGE = {
  number: '06 · Coverage',
  title: 'Based in Oslo. Available across Norway.',
  copy:
    'I am based in Oslo and can support productions in Oslo, Eastern Norway, and other parts of Norway by agreement.',
}

export const COVERAGE_AREAS: string[] = [
  'Oslo',
  'Lillestrøm',
  'Gardermoen / Oslo Airport area',
  'Bergen',
  'Stavanger',
  'Trondheim',
  'Tromsø',
  'Lofoten',
  'Norwegian fjords',
  'Mountain and forest locations',
  'Coastal and maritime environments',
  'Corporate and industrial locations',
]

/* ── Contact ───────────────────────────────────────────────────────── */

export interface FaqItem {
  question: string
  answer: string
}

/** Aligned with Norwegian fixer / production-service FAQ patterns (permits, crew, lead time, visas, coverage) */
export const FAQ_INTRO = {
  number: '07 · FAQ',
  title: 'Norway production questions.',
  copy:
    'Practical answers for international teams planning a shoot in Norway. For project-specific questions, send a brief and I will respond with clear next steps.',
}

export const FAQ_ITEMS: FaqItem[] = [
  {
    question: 'Do you help with filming permits in Norway?',
    answer:
      'Yes. I help assess whether a permit is needed, contact municipalities and landowners, and coordinate access for streets, waterfronts, offices, civic settings, drones, and controlled public setups. Oslo often needs explicit permits; many low-impact public shoots outside Oslo may not — but road closures, traffic control, and motorized vehicles on uncultivated land usually do.',
  },
  {
    question: 'Do I need a permit for every shoot in Norway?',
    answer:
      'Not always. Minimal-impact filming in public areas may not require a permit unless you restrict access, close roads, use traffic control, or operate motorized vehicles on uncultivated land. Private property still needs owner agreement. I map permit needs early so the schedule stays realistic.',
  },
  {
    question: 'Can you source local crew and equipment?',
    answer:
      'Yes. I help match Oslo-based and Norway-wide crew — producer, camera, sound, lighting, production assistants, drivers, translators, drone operators — plus equipment rental, studios, transport, and catering based on the brief and footprint.',
  },
  {
    question: 'How far in advance should we contact you?',
    answer:
      'The earlier the better for locations, permits, contributors, and peak-season logistics. For a short feasibility check, a rough brief is enough to start. Tight timelines are possible when the scope is clear and permit needs are realistic.',
  },
  {
    question: 'Do international crew need work permits to film in Norway?',
    answer:
      'Non-Norwegian residents may generally work in Norway for up to 90 days without a work permit, with on-screen filming in the final production limited to 14 working days per person (prep and post are counted differently). Longer shoots or more on-screen days may require permits — I help flag this during planning.',
  },
  {
    question: 'Can you support shoots outside Oslo?',
    answer:
      'Yes. I am based in Oslo and regularly support productions across Norway — Bergen, Stavanger, Trondheim, Tromsø, Lofoten, fjord regions, coast, mountains, and industrial sites — by agreement. Travel, weather windows, and local vendors are planned as part of the brief.',
  },
  {
    question: 'Do you work with international productions filming in Norway?',
    answer:
      'Yes. Most of my clients are based outside Norway. I help international teams navigate locations, permits, crew, translation, and local logistics — whether you are a small crew or a larger unit shooting across the country.',
  },
]

export const CONTACT = {
  number: '08 · Contact',
  title: 'Need production support in Norway?',
  copy:
    'Send your production brief and I’ll help you plan the Norway side of your shoot. Send a short brief with dates, location needs, crew size, and what you need help with — I’ll respond with clear next steps.',
  roleLine: 'Norway-based fixer, producer, and production coordinator',
  locationLine: 'Based in Oslo, Norway',
  briefTitle: 'What you can send me',
  briefCopy: 'To get started, send what you have. A rough outline is enough to start.',
}

export const BRIEF_ITEMS: string[] = [
  'Production type',
  'Shoot dates',
  'Preferred location or region',
  'Crew size',
  'Location needs',
  'Contributor or talent needs',
  'Permit needs, if known',
  'Drone needs, if any',
  'Equipment needs',
  'Budget range, if available',
  'Main contact person',
  'Treatment, script, moodboard, or reference material',
]

export const CONTACT_LINKS = [
  { label: 'Email', value: email, href: FIXER_BRIEF_FORM_HASH },
  { label: 'Phone', value: phone, href: phoneTel },
  { label: 'WhatsApp', value: '+47 94 44 64 46', href: whatsappUrl },
  { label: 'LinkedIn', value: 'linkedin.com/in/farzad-bayat', href: linkedinUrl },
  { label: 'Website', value: CONTACT_WEBSITE_DISPLAY, href: FIXER_PAGE_PATH },
]

export const FOOTER_LINKS: NavLink[] = [
  { label: 'F.B/c Home', href: '/' },
  { label: 'About', href: '/about' },
  { label: 'Privacy', href: '/privacy' },
  { label: 'Terms', href: '/terms' },
]

/* ── JSON-LD ───────────────────────────────────────────────────────── */

export function buildFixerStructuredData() {
  const pageUrl = PAGE_URL
  const ogImage = PAGE_OG_IMAGE

  const professionalService = {
    '@context': 'https://schema.org',
    '@type': 'ProfessionalService',
    name: 'Farzad Bayat — Norway Fixer & Production Services',
    image: ogImage,
    url: pageUrl,
    telephone: phone,
    email: email,
    address: {
      '@type': 'PostalAddress',
      addressLocality: 'Oslo',
      addressCountry: 'NO',
    },
    areaServed: {
      '@type': 'Country',
      name: 'Norway',
    },
    serviceType:
      'Norway fixer, local producer, production coordinator, location coordination, permits, logistics',
    description: PAGE_DESCRIPTION,
  }

  const faqPage = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: FAQ_ITEMS.map(item => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.answer,
      },
    })),
  }

  return [professionalService, faqPage]
}
